import { BookOpenIcon } from '@heroicons/react/24/outline';

import Footer from '@/renderer/Footer';
import Header from '@/renderer/Header';
import { usePageContext } from '@/renderer/utils/usePageContext';
import { ArrowRightCircleIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/16/solid';

const GUIDE = [
  {
    section: 'About RAS',
    content: [
      {
        title: 'What is Right Angle Sports (RAS)?',
        description: (
          <>
            RAS is a professional sports betting service known for consistently beating the market
            over a long period of time. We specialize in betting sides and totals across multiple
            sports and have built our reputation through sharp, disciplined processes and fully
            transparent public results. We are not a tout service&mdash;we are bettors who win and
            allow others to follow our plays.
          </>
        ),
      },
      {
        title: 'What makes RAS different from other betting services?',
        description: (
          <>
            Most pick-sellers focus on hype, unrealistic promises, and volume. We don’t. Our focus
            is edge, execution, and education. Our plays are real bets we make ourselves, released
            only when we believe they are strong enough to beat the market at widely available
            lines. Everything we do is rooted in professional-level research, collaboration, and
            market discipline.
          </>
        ),
      },
      {
        title: 'Who are the people behind RAS?',
        description: (
          <>
            We are a team of professional bettors. Some of us are originators who create numbers
            from scratch. Others are specialists in film breakdown, tempo analysis, or injury
            research. Everyone on the team is here for one purpose: to find value. We’ve spent years
            refining our workflow, and our collaboration is one of our biggest strengths.
          </>
        ),
      },
      {
        title: 'How does the team work behind the scenes?',
        description: (
          <>
            We divide games by conferences and watch everything&mdash;live or replayed. Everyone
            submits bets and writeups independently, then we meet to compare notes. If we agree on a
            game, it rises to the top. If there’s disagreement, we debate it. The best arguments
            win. Consensus is earned.
          </>
        ),
      },
      {
        title: 'What sports do you cover?',
        description: (
          <>
            Historically, we’ve covered college football, NFL, college basketball, and WNBA.
            Depending on the season and market edge, we may expand or contract offerings. Every
            sport we cover is treated with the same rigor.
          </>
        ),
      },
    ],
  },
  {
    section: 'Philosophy & Process',
    content: [
      {
        title: 'What’s the difference between a release and a bet you don’t release?',
        description: (
          <>
            We bet more games than we release. A “release” means we believe the play is strong and
            bettable at widely available numbers. It’s a combination of confidence and logistics.
            Some bets may still have edge, but we keep them in-house if they’re borderline or the
            line has already moved.
          </>
        ),
      },
      {
        title: 'What is line value, and how does RAS find it?',
        description: (
          <>
            Line value is the gap between our true number and the market number. We build that edge
            by watching games, gathering injury info, understanding tempo, and knowing which stats
            are predictive and which aren’t. It’s not just math&mdash;it’s context, matchups,
            coaching, and style of play.
          </>
        ),
      },
      {
        title: 'What does it mean to “beat the closing line”?',
        description: (
          <>
            The closing line is the final number a sportsbook offers before a game starts. It
            reflects the market’s collective wisdom. If your bet beats that number consistently,
            you’re doing something right. Over time, beating the closing line is one of the best
            indicators of sustainable, winning betting.
          </>
        ),
      },
      {
        title: 'Why is getting the best number so important?',
        description: (
          <>
            If we release -3 (-110) and you bet -3 (-120) or -3.5, it’s not the same bet. That
            difference affects win probability&mdash;and your long-term edge. The market is the
            market. You can’t follow anyone successfully if you’re always late to the number.
          </>
        ),
      },
      {
        title: 'Do you use models or is it all film-based?',
        description: (
          <>
            We use both. Some plays are driven by proprietary models. Others are based on film,
            tempo analysis, coaching tendencies, or matchup dynamics. Our strength is that we blend
            data with real-world context. It’s not one formula&mdash;it’s a team of thinkers.
          </>
        ),
      },
      {
        title: 'Why do you re-watch games when you already saw them live?',
        description: (
          <>
            Live viewing gives us raw impressions. Re-watching gives us context and clarity. We
            might miss a subtle injury, change in tempo, or coaching shift during a live broadcast.
            The second pass often reveals what the scoreboard obscures.
          </>
        ),
      },
      {
        title: 'Why does RAS focus more on sides early in the week and totals later in the week?',
        description: (
          <>
            Sides often stabilize by Monday&mdash;limits are higher, the market has settled, and our
            edge becomes clearer. Totals are more fragile, often influenced by weather, tempo
            mismatches, or injury reports that emerge later. The timing isn’t arbitrary&mdash;it’s
            strategic.
          </>
        ),
      },
      {
        title: 'How do you balance data with “feel” or qualitative observations?',
        description: (
          <>
            We never lean on feel alone, but data without context is dangerous. For example, a team
            might look elite statistically but struggle against specific styles. Film tells us{' '}
            <i>how</i> the numbers happened. The edge is often in the nuance between the two.
          </>
        ),
      },
    ],
  },
  {
    section: 'Results & Expectations',
    content: [
      {
        title: 'How transparent is RAS about performance?',
        description: (
          <>
            Completely. Our track record is published and tracked in real-time. There are no edits,
            no deletions, no hidden records. Winning long-term in sports betting is rare, and we
            hold ourselves to a public standard that reflects that. We are monitored by third-party
            platform{' '}
            <a
              href="https://www.betstamp.app/u/RAS"
              target="_blank"
              className="text-emerald-600 underline dark:text-emerald-500"
              rel="noreferrer"
            >
              <span>betstamp</span>
              <ArrowTopRightOnSquareIcon className="ml-1 inline-block size-4" />
            </a>
            .
          </>
        ),
      },
      {
        title: 'How long should I follow RAS before judging performance?',
        description: (
          <>
            Judge over seasons, not weeks. This is a long-term game. One month isn’t enough. Ten
            plays isn’t enough. Our record speaks over thousands of bets. If you’re serious, think
            in months, seasons, years&mdash;not weekends.
          </>
        ),
      },
      {
        title: 'Is a 55% win rate really that good?',
        description: (
          <>
            It’s world-class. A 55% win rate at -110 odds means a significant profit margin over
            time. Most bettors lose. Most pick-sellers don’t even beat 50%. If you can win 55%, with
            volume and discipline, that’s how professionals make a living.
          </>
        ),
      },
      {
        title: 'What are realistic expectations for my betting results?',
        description: (
          <>
            Expect long-term, steady profit&mdash;not weekly jackpots. Some weeks you’ll win, some
            you’ll lose. But over the course of a season, if you follow the process, respect the
            numbers, and manage your bankroll, you’re a favorite to be ahead following our plays.
          </>
        ),
      },
      {
        title: 'What happens during a losing streak?',
        description: (
          <>
            We keep doing the work. Losing streaks happen in betting&mdash;it’s part of the math. We
            review our process, watch for mistakes, and adjust if needed. But variance is real.
            We’ve had rough stretches followed by historic runs. The key is staying disciplined.
          </>
        ),
      },
    ],
  },
  {
    section: 'Betting Education',
    content: [
      {
        title: 'What does variance mean in betting?',
        description: (
          <>
            Variance is the natural fluctuation in results. Even with a 55% win rate (which is
            elite), you’ll see losing streaks. That’s part of betting. You need to have the bankroll
            and the mindset to endure bad runs. Variance doesn’t mean something is broken&mdash;it
            means you’re playing a game with built-in swings.
          </>
        ),
      },
      {
        title: 'What’s something casual bettors consistently overlook?',
        description: (
          <>
            Game state. Many people don’t understand how drastically a team’s approach shifts
            depending on score, quarter, and opponent. A team may look fast-paced on paper, but
            that’s because they trailed in five games and had to chase. We isolate intent from
            outcome.
          </>
        ),
      },
      {
        title: 'Why don’t you talk about “locks” like other services?',
        description: (
          <>
            Because those terms are marketing nonsense. There are no locks. Every play carries risk.
            The vast majority of our plays (over 95%) are graded at 1 unit. We feel this is the
            best/fairest way to properly evaluate our results.
          </>
        ),
      },
    ],
  },
  {
    section: 'Using the Service',
    content: [
      {
        title: 'How are picks delivered to me?',
        description: (
          <>
            You will receive a release warning&mdash;typically by text or email or both&mdash;which
            notifies you of when a play will be officially sent out. You will log in to your
            subscriber dashboard and wait for the play.
          </>
        ),
      },
      {
        title: 'When are releases sent out?',
        description: (
          <>
            It depends on the sport. For college football and NFL, most sides go out Monday morning
            when the markets mature. Totals are usually later in the week. We release when limits
            are reasonable and the line is still widely available. You’ll be told in advance what
            time to expect it.
          </>
        ),
      },
      {
        title: 'How fast do I need to act when a pick is released?',
        description: (
          <>
            <p>
              Immediately. Our releases move the market&mdash;sometimes within seconds. We give you
              a countdown so you can be logged in and ready, but speed is critical. The closer you
              get to the release number, the more edge you retain. Seconds matter.
            </p>
            <p>
              We recommend using a desktop when possible and having two or more devices ready to get
              releases and place bets efficiently.
            </p>
            <p>
              There are little things you can do to make the process faster. Copy and paste bet
              amounts, have passwords saved in your browser, use the "Find" (Control-F) feature to
              locate game numbers or team names on the screen, everything helps.
            </p>
          </>
        ),
      },
      {
        title: 'What happens if I miss a release?',
        description: (
          <>
            <p>
              If the line is gone, skip the bet&mdash;unless the current line is still within the
              play-through number. Betting bad numbers will kill your edge. The worst thing you can
              do is chase. You won’t catch up by forcing bets that no longer have value.
            </p>
            <p>
              When available, 1st half lines, money lines and alt-lines will often not move as
              quickly as traditional full game prices but these bets typically have comparable +EV
              over the long run.
            </p>
          </>
        ),
      },
      {
        title: 'What if I don’t see the same line you released?',
        description: (
          <>
            Books differ. That’s why having multiple outs matters. We only release plays when the
            number is widely available at the time of release, but within seconds, some books will
            move. The more outs you have, the better chance you’ll match the number.
          </>
        ),
      },
      {
        title: 'Can I bet these plays on mobile apps or only in Vegas?',
        description: (
          <>
            You can absolutely bet from mobile apps&mdash;FanDuel, DraftKings, Circa, BetMGM, etc.
            You’ll get the best results if you have access to multiple books and can line shop
            efficiently. This service is designed to work for modern bettors, not just Vegas sharps.
          </>
        ),
      },
      {
        title: 'Do you recommend line shopping?',
        description: (
          <>
            Yes&mdash;strongly. The more outs you have, the more likely you are to match our release
            number. Getting -3 instead of -3.5, or +7 instead of +6.5, is often the difference
            between a win and a push&mdash;or a win and a loss. This adds up over a season.
          </>
        ),
      },
      {
        title:
          'How can I lower the chances of getting my account banned or limited from playing RAS releases?',
        description: (
          <>
            Mix up your action, don't play only RAS releases in any one account. Spread out your
            action. If we have 3 plays, ideally you would bet 1 release each in 3 different
            accounts. We realize this is not always realistic, but you get the idea. Don't bet the
            max. If your max is $1000, choose a lower amount. No matter what you do, once you win
            enough from any sportsbook you are likely to get limited or banned, so be proactive and
            have backup accounts ready to go.
          </>
        ),
      },
    ],
  },
  {
    section: 'Bankroll & Bet Sizing',
    content: [
      {
        title: 'How much should I bet on each play?',
        description: (
          <>
            Most users follow a flat-betting system&mdash;betting 1 unit per release. Some adjust
            bet size slightly based on confidence or bankroll, but we recommend you stick to a
            consistent stake that fits your risk tolerance. The goal is long-term growth, not
            chasing big wins.
          </>
        ),
      },
    ],
  },
  {
    section: 'RAS Team',
    content: [
      {
        title: 'What’s your biggest edge as a team?',
        description: (
          <>
            Collaboration. We have originators, film guys, data guys, market readers—all working in
            sync. No one’s relying on just one angle. That blend lets us spot value where others
            only see noise.
          </>
        ),
      },
      {
        title:
          'What’s the benefit of multiple team members evaluating games independently before collaborating?',
        description: (
          <>
            It minimizes groupthink. When multiple people arrive at the same conclusion
            independently, that convergence adds confidence. But we also want internal
            disagreement—it forces us to defend our positions and refine our edge. Consensus isn’t a
            shortcut—it’s a filter.
          </>
        ),
      },
      {
        title: 'How does the team work behind the scenes?',
        description: (
          <>
            We divide games by conferences and watch everything—live or replayed. Everyone submits
            bets and writeups independently, then we meet to compare notes. If we agree on a game,
            it rises to the top. If there’s disagreement, we debate it. The best arguments win.
            Consensus is earned.
          </>
        ),
      },
    ],
  },
];

export default function WelcomeGuide() {
  const {
    config: { title },
  } = usePageContext();

  return (
    <div className="flex h-40 min-h-[stretch] flex-1 grow flex-col justify-between overflow-hidden">
      <Header title="Welcome Guide" Icon={BookOpenIcon} className="py-2 md:hidden" />
      <div className="overflow-auto">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="relative mx-auto hidden max-w-2xl pt-20 pb-24 text-center sm:block">
            <h1 className="font-bold text-3xl text-zinc-900 tracking-tight sm:text-4xl dark:text-zinc-200">
              {title}
            </h1>
            <p className="mt-4 text-base text-zinc-600 leading-7 dark:text-zinc-400">
              Last updated on May 28, 2025
            </p>
          </div>
        </div>
        <div className="relative mt-8 px-4 sm:mt-0 sm:px-6 lg:px-8">
          <div className="prose prose-sm prose-zinc dark:prose-invert mx-auto max-w-prose">
            <ul className="list-none pl-0">
              {GUIDE.map((section) => (
                <li key={section.section} className="mb-8">
                  <h2 className="group font-semibold text-lg">
                    <a
                      href={`#${section.section.replace(/\s+/g, '-').toLowerCase()}`}
                      className="text-zinc-700 no-underline group-hover:text-emerald-500 dark:text-white dark:group-hover:text-emerald-400"
                    >
                      <div className="inline-flex items-center">
                        <span>{section.section}</span>
                        <ArrowRightCircleIcon className="ml-1 size-4 align-middle text-zinc-600 group-hover:text-emerald-500 dark:text-zinc-400 dark:group-hover:text-emerald-400" />
                      </div>
                    </a>
                  </h2>
                  <ul className="mt-4 list-disc space-y-4 pl-4 marker:text-zinc-500">
                    {section.content.map((item) => (
                      <li key={item.title} className="mb-2">
                        <a
                          href={`#${item.title.replace(/\s+/g, '-').toLowerCase()}`}
                          className="group text-sm text-zinc-700 no-underline hover:text-emerald-500 dark:text-white dark:hover:text-emerald-400"
                        >
                          {(() => {
                            const words = item.title.split(' ');
                            const lastWord = words.pop();
                            return (
                              <>
                                {words.join(' ')}
                                {words.length > 0 && ' '}
                                {/* Use non-breaking space and inline-flex for icon+lastWord */}
                                <span className="inline-flex items-center">
                                  {lastWord}
                                  {'\u00A0'}
                                  <ArrowRightCircleIcon className="ml-1 size-4 align-middle text-zinc-600 group-hover:text-emerald-500 dark:text-zinc-400 dark:group-hover:text-emerald-400" />
                                </span>
                              </>
                            );
                          })()}
                        </a>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
            {GUIDE.map((section) => (
              <div key={section.section}>
                <hr className="my-20 border-zinc-900/20 dark:border-white/20" />
                <h2>{section.section}</h2>
                {section.content.map((item) => (
                  <div
                    key={item.title}
                    id={item.title.replace(/\s+/g, '-').toLowerCase()}
                    className="mb-8"
                  >
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}
